import React, {FC} from 'react';
import styled from 'styled-components';

import {Icon, IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {UserTheme} from '../../../../../../libs/shared/ui/src/theme/themeTypes';
import {
  ConversationStatusesEnum,
  UnreadStatusesEnum,
} from '../../../../../helpers/models/conversationHelpers';

/*
 * Props.
 */

export interface CellStatusIconProps {
  isSelected: boolean | undefined;
  unreadStatus: UnreadStatusesEnum;
  status: ConversationStatusesEnum;
  reminderDate: number | undefined;
  className?: string;
}

interface IconStyleProps {
  $isSelected: boolean | undefined;
  $unreadStatus: UnreadStatusesEnum;
  $status: ConversationStatusesEnum;
  $reminderDate: number | undefined;
}

/*
 * Style.
 */

export const StyledCellStatusIconBaseDiv = styled.div<IconStyleProps>`
  color: ${(p) =>
    getConversationStatusIconColor(p.theme, p.$unreadStatus, p.$status, p.$isSelected, p.$reminderDate)};
`;

/*
 * Component.
 */

export const CellStatusIcon: FC<CellStatusIconProps> = (props) => {
  const {isSelected, reminderDate, status, unreadStatus, className} = props;
  const iconName = getConversationStatusIconName(status, reminderDate);

  if (
    (status === ConversationStatusesEnum.ARCHIVED ||
      status === ConversationStatusesEnum.TRASHED ||
      status === ConversationStatusesEnum.SPAM) &&
    iconName
  ) {
    return (
      <StyledCellStatusIconBaseDiv
        $isSelected={isSelected}
        $status={status}
        $unreadStatus={unreadStatus}
        $reminderDate={reminderDate}
        className={className}
      >
        <Icon name={iconName} />
      </StyledCellStatusIconBaseDiv>
    );
  }

  return null;
};

/*
 * Helpers.
 */

/** Find the status icon color to display based on the unread/conversation status. */
function getConversationStatusIconColor(
  theme: UserTheme,
  unreadStatus: UnreadStatusesEnum,
  status: ConversationStatusesEnum,
  isSelected: boolean | undefined,
  reminderDate: number | undefined,
) {
  if (isSelected) {
    return theme.alphas.white50;
  }

  const isSnoozeUnread = unreadStatus === UnreadStatusesEnum.SNOOZE_UNREAD;
  if (isSnoozeUnread || (status === ConversationStatusesEnum.ARCHIVED && reminderDate)) {
    return theme.palette.orange.shade40;
  }

  if (status === ConversationStatusesEnum.ARCHIVED) {
    return theme.palette.green.shade40;
  }

  if (status === ConversationStatusesEnum.TRASHED) {
    return theme.palette.red.shade40;
  }

  if (status === ConversationStatusesEnum.SPAM) {
    return theme.palette.purple.shade40;
  }

  // Base state.
  return theme.greys.shade60;
}

function getConversationStatusIconName(
  status: ConversationStatusesEnum,
  reminderDate: number | undefined,
): IconName | undefined {
  switch (status) {
    case ConversationStatusesEnum.ARCHIVED: {
      if (reminderDate) {
        return 'snoozeFilledSmall';
      }
      return 'archiveFilledSmall';
    }

    case ConversationStatusesEnum.TRASHED:
      return 'trashFilledSmall';

    case ConversationStatusesEnum.SPAM:
      return 'spamFilledSmall';

    default:
      return undefined;
  }
}
